import * as React from 'react'
import MuiAvatar from '@mui/material/Avatar'
import MuiListItemAvatar from '@mui/material/ListItemAvatar'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Select from '@mui/material/Select'
import {styled} from '@mui/material/styles'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import {Box, Typography} from '@mui/material'
import {
    useGetCurrentUserQuery,
    useGetUserAccountsQuery,
    useSetUserAccountMutation,
    useUnsetUserAccountMutation,
} from 'store/Store'
import {useTranslation} from 'react-i18next'
import {useMemo, useCallback} from 'react'
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded'
const Avatar = styled(MuiAvatar)(({theme}) => ({
    width: 28,
    height: 28,
    backgroundColor: (theme.vars || theme).palette.background.paper,
    color: (theme.vars || theme).palette.text.secondary,
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
}))

const ListItemAvatar = styled(MuiListItemAvatar)({
    minWidth: 0,
    marginRight: 12,
})

const StyledSelect = styled(Select)(({theme}) => ({
    minWidth: 215,
    minHeight: 56,
    '& .MuiSelect-icon': {
        color: (theme.vars || theme).palette.text.secondary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
}))

const TriggerContent = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 56,
}))

export default function SelectContent() {
    const {data: userAccounts} = useGetUserAccountsQuery()
    const {data: userToken} = useGetCurrentUserQuery()
    const [doSetUserAccount] = useSetUserAccountMutation()
    const [doUnsetUserAccount] = useUnsetUserAccountMutation()

    const {t} = useTranslation()
    const handleChange = (event) => {
        const value = event?.target?.value
        if (value === '') {
            doUnsetUserAccount()
        } else {
            doSetUserAccount({accountId: value})
        }
    }

    const accountsById = useMemo(() => {
        if (!userAccounts) return new Map()
        return new Map(userAccounts.map((account) => [account?._id, account]))
    }, [userAccounts])

    const selectedAccountId = userToken?.account?._id ?? ''
    const isClearSiteEnable = useMemo(
        () => userToken.isSuperUser && selectedAccountId,
        [userToken.isSuperUser, selectedAccountId]
    )
    const listItems = useMemo(() => {
        const items = (userAccounts ?? []).map((account, index) => {
            const isSelected = selectedAccountId === account?._id

            return (
                <MenuItem
                    value={account?._id}
                    key={`account_${index}`}
                    dense
                    sx={{
                        borderRadius: 1.5,
                        mx: 1,
                        my: 0.5,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                    }}>
                    <ListItemAvatar>
                        <Avatar alt={account?.name}>
                            <DevicesRoundedIcon sx={{fontSize: '1rem'}} />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={account?.name}
                        secondary={account?.location}
                        primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: isSelected ? 600 : 500,
                            noWrap: true,
                        }}
                        secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                            noWrap: true,
                        }}
                    />
                    {isSelected && (
                        <CheckRoundedIcon color="primary" fontSize="small" sx={{ml: 'auto'}} />
                    )}
                </MenuItem>
            )
        })

        return items
    }, [selectedAccountId, userAccounts])

    const menuProps = useMemo(
        () => ({
            PaperProps: {
                elevation: 3,
                sx: {
                    mt: 1,
                    borderRadius: 2,
                    minWidth: 260,
                    py: 0.5,
                },
            },
            MenuListProps: {
                sx: {
                    py: 0,
                },
            },
        }),
        []
    )

    const renderSelectedValue = useCallback(
        (selected) => {
            const account = accountsById.get(selected)

            if (!account) {
                return (
                    <TriggerContent>
                        <Avatar alt="Select account">
                            <DevicesRoundedIcon sx={{fontSize: '1rem'}} />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {t('side_menu.select_site', 'Select site')}
                        </Typography>
                    </TriggerContent>
                )
            }

            return (
                <TriggerContent>
                    <Avatar alt={account?.name}>
                        <DevicesRoundedIcon sx={{fontSize: '1rem'}} />
                    </Avatar>
                    <Box display="flex" flexDirection="column" overflow="hidden">
                        <Typography variant="body2" noWrap>
                            {account?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {account?.location}
                        </Typography>
                    </Box>
                </TriggerContent>
            )
        },
        [accountsById, t]
    )

    return (
        <StyledSelect
            id="site-select"
            value={selectedAccountId}
            onChange={handleChange}
            displayEmpty
            IconComponent={UnfoldMoreRoundedIcon}
            renderValue={renderSelectedValue}
            MenuProps={menuProps}
            SelectDisplayProps={{
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                    width: '100%',
                },
            }}
            fullWidth>
            {isClearSiteEnable && (
                <MenuItem
                    value=""
                    key="account_clear"
                    dense
                    sx={{
                        borderRadius: 1.5,
                        mx: 1,
                        mb: 0.5,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        color: 'text.secondary',
                    }}>
                    <ListItemAvatar>
                        <Avatar
                            alt="Clear selection"
                            sx={{
                                bgcolor: 'action.selected',
                                color: 'text.secondary',
                            }}>
                            <ClearRoundedIcon sx={{fontSize: '1rem'}} />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={t('side_menu.clear_site', 'Clear selection')}
                        primaryTypographyProps={{variant: 'body2', fontWeight: 500}}
                    />
                </MenuItem>
            )}
            <ListSubheader
                disableSticky
                sx={{
                    // pt: 0.5,
                    // pb: 1,
                    // px: 3,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                }}>
                {t('side_menu.sites')}
            </ListSubheader>
            {listItems}
        </StyledSelect>
    )
}
